import React from "react";
import { Typography, IconButton, makeStyles, FormLabel } from "@material-ui/core";
import { FileUpload, Close } from "mdi-material-ui";
import cn from "classnames";
import { DnDLocalContainer } from "@containers/DnDContainer/DnDLocalContainer";

export type FileUploadFieldValue = { name: string; file?: File; id?: string } | null;

interface FileUploadFieldProps {
  value: FileUploadFieldValue;
  error?: string;
  onChange: (file: FileUploadFieldValue) => void;
  acceptDropzoneTypes?: Record<string, string[]>;
  label?: string;
  helperText?: string;
  allowedFormatsText?: string;
  disabled?: boolean;
  required?: boolean;
  clearable?: boolean;
}

export function FileUploadField({
  value,
  error,
  onChange,
  acceptDropzoneTypes = {
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"]
  },
  label = "Ladda upp fil",
  helperText = "Dra och släpp fil här eller klicka för att välja",
  allowedFormatsText = "Tillåtna filformat: PDF, JPG, PNG, DOC, DOCX",
  disabled = false,
  clearable = true,
  required
}: FileUploadFieldProps) {
  const classes = useStyles();

  const handleFileDrop = (files: File[]) => {
    if (disabled || files.length === 0) return;

    const file = files[0];

    onChange({ name: file.name, file });
  };

  const handleFileRemove = () => {
    if (disabled) return;
    onChange(null);
  };

  if (value) {
    return (
      <div className={classes.selectedContainer}>
        <div className={classes.fileDisplayContainer}>
          <Typography variant="body2" className={classes.fileName}>
            Vald fil: {value.name}
          </Typography>
          {!disabled && clearable && (
            <IconButton
              size="small"
              onClick={handleFileRemove}
              className={classes.removeButton}
              title="Ta bort fil"
              disabled={disabled}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </div>
      </div>
    );
  }

  return (
    <DnDLocalContainer
      onDrop={handleFileDrop}
      dropzoneOptions={{
        accept: acceptDropzoneTypes,
        multiple: false,
        disabled
      }}
      noClick={disabled}
      className={cn([
        classes.uploadContainer,
        error && classes.uploadContainerError,
        disabled && classes.uploadContainerDisabled
      ])}
    >
      <FormLabel required={required} error={!!error} className={classes.uploadLabel}>
        <FileUpload className={classes.uploadIcon} />
        {label}
      </FormLabel>
      <Typography variant="body2" color="textSecondary" className={classes.helperText}>
        {helperText}
      </Typography>
      <Typography variant="caption" display="block" className={classes.formatText}>
        {allowedFormatsText}
      </Typography>
    </DnDLocalContainer>
  );
}

const useStyles = makeStyles((theme) => ({
  uploadContainer: {
    border: "2px dashed #ccc",
    borderRadius: 4,
    padding: 16,
    textAlign: "center",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#FEECE1"
    }
  },
  uploadContainerError: {
    borderColor: theme.palette.error.main
  },
  uploadContainerDisabled: {
    borderColor: theme.palette.action.disabledBackground,
    cursor: "not-allowed",
    opacity: 0.5,
    "&:hover": {
      backgroundColor: "transparent"
    }
  },
  selectedContainer: {
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    padding: 16,
    textAlign: "center",
    backgroundColor: "#f5f5f5"
  },
  fileDisplayContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8
  },
  fileName: {
    fontWeight: "bold"
  },
  removeButton: {
    padding: 4,
    color: "#f44336"
  },
  helperText: {
    marginTop: 8
  },
  formatText: {
    marginTop: 4
  },
  uploadButton: {
    fontWeight: 700,
    "&:hover": {
      backgroundColor: "transparent"
    }
  },
  uploadLabel: {
    cursor: "inherit",
    fontWeight: 700,
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18
  },
  uploadIcon: {
    fontSize: 20,
    marginRight: 6
  }
}));
